import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SimpleGrid, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { TaskType } from '__generated__/GQL';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import Text from 'components/_core/Text';
import TaskTypeSelect from 'features/Work/components/TaskTypeSelect';
import VatInput from 'features/Work/components/VatInput';

import {
  CatalogItemFormSchema,
  catalogItemFormSchema,
} from '../../catalogItemForm.schema';
import locales from './locales';

const getDefaultValues = (
  initialValues?: CatalogItemFormSchema,
): CatalogItemFormSchema => ({
  subtitle: initialValues?.subtitle ?? null,
  title: initialValues?.title ?? '',
  type: initialValues?.type ?? TaskType.Service,
  unitPrice: initialValues?.unitPrice ?? 0,
  vatPercentage: initialValues?.vatPercentage ?? 20,
});

interface BaseModalProps extends SunshineModalProps {
  description: string;
  handleSave: (values: CatalogItemFormSchema) => Promise<void>;
  initialValues?: CatalogItemFormSchema;
  isLoading?: boolean;
  title: string;
}

const BaseModal: FC<BaseModalProps> = ({
  children,
  description,
  handleSave,
  initialValues,
  isLoading,
  isOpen,
  onClose,
  title,
  ...modalProps
}) => {
  const {
    control,
    formState: { errors, isValid },
    getValues,
    register,
    reset,
  } = useForm<CatalogItemFormSchema>({
    defaultValues: getDefaultValues(initialValues),
    mode: 'onChange',
    resolver: zodResolver(catalogItemFormSchema),
  });

  useEffect(() => {
    reset(getDefaultValues(initialValues));
  }, [initialValues, isOpen, reset]);

  return (
    <SunshineModal {...modalProps} isOpen={isOpen} onClose={onClose}>
      <SunshineModal.Header />

      <SunshineModal.Body>
        <SunshineModal.Title>{title}</SunshineModal.Title>

        <Text marginBottom="space-32">{description}</Text>

        <VStack align="stretch" spacing="space-16">
          <Form.Field error={errors.title?.message} label={locales.form.name}>
            <Form.Input {...register('title')} isDisabled={isLoading} />
          </Form.Field>

          <Form.Field label={locales.form.description}>
            <Form.Textarea {...register('subtitle')} isDisabled={isLoading} />
          </Form.Field>

          <Controller
            control={control}
            name="type"
            render={({ field, fieldState: { error } }) => (
              <Form.Field error={error?.message} label={locales.form.type}>
                <TaskTypeSelect
                  isDisabled={isLoading}
                  onChange={(option) => field.onChange(option?.value)}
                  value={field.value}
                />
              </Form.Field>
            )}
          />

          <SimpleGrid columns={2} gap="space-8">
            <Controller
              control={control}
              name="unitPrice"
              render={({ field, fieldState: { error } }) => (
                <Form.Field error={error?.message} label={locales.form.price}>
                  <Form.Input
                    {...field}
                    isDisabled={isLoading}
                    onChange={({ target: { value } }) =>
                      field.onChange(value === '' ? value : Number(value))
                    }
                    rightIcon="euro"
                    type="number"
                  />
                </Form.Field>
              )}
            />

            <Controller
              control={control}
              name="vatPercentage"
              render={({ field, fieldState: { error } }) => (
                <Form.Field error={error?.message} label={locales.form.vat}>
                  <VatInput
                    isDisabled={isLoading}
                    menuPlacement="top"
                    onChange={field.onChange}
                    value={field.value}
                  />
                </Form.Field>
              )}
            />
          </SimpleGrid>
        </VStack>
      </SunshineModal.Body>

      <SunshineModal.Footer>
        {children}

        <Button
          icon="arrow-right"
          isDisabled={!isValid}
          isLoading={isLoading}
          onClick={() => handleSave(getValues())}
        >
          {locales.save}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default BaseModal;
